import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as opsApi from "@/features/ops/api";
import type { CodeSequence, CodeTally } from "@/features/ops/api";
import { formatCents } from "@/lib/utils";

/**
 * Transcript Code Lab — analytics over parsed transcript transaction codes:
 * tally a code's occurrences across every firm, or find start→end code
 * sequences within N days, each drilling down to the owning transcript.
 */
export default function CodesPage() {
  const [code, setCode] = useState("");
  const [tally, setTally] = useState<CodeTally | null>(null);
  const [tallyBusy, setTallyBusy] = useState(false);

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [days, setDays] = useState("365");
  const [seq, setSeq] = useState<CodeSequence | null>(null);
  const [seqBusy, setSeqBusy] = useState(false);

  // Show the most-common codes on first load so the lab isn't empty.
  useEffect(() => {
    void opsApi.tallyCode("").then(setTally).catch(() => {});
  }, []);

  async function runTally() {
    setTallyBusy(true);
    try {
      setTally(await opsApi.tallyCode(code));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not tally the code.");
    } finally {
      setTallyBusy(false);
    }
  }

  async function runSequence() {
    if (!start.trim() || !end.trim()) {
      toast.error("Enter both a start and end code.");
      return;
    }
    setSeqBusy(true);
    try {
      setSeq(await opsApi.sequenceCodes(start, end, Number(days) || 365));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not run the sequence match.");
    } finally {
      setSeqBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Tally mode */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tally mode</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1">
              <Label htmlFor="tally-code">Transaction code</Label>
              <Input
                id="tally-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void runTally()}
                placeholder="e.g. 150"
                className="w-40"
              />
            </div>
            <Button type="button" onClick={() => void runTally()} disabled={tallyBusy}>
              {tallyBusy ? "Counting…" : "Count"}
            </Button>
          </div>

          {tally?.code == null && (tally?.topCodes.length ?? 0) > 0 && (
            <div>
              <p className="text-muted-foreground mb-2 text-sm">
                Most frequent codes across all transcripts:
              </p>
              <div className="flex flex-wrap gap-2">
                {tally!.topCodes.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    className="bg-muted hover:bg-accent rounded-md px-2.5 py-1 text-xs font-medium"
                    onClick={() => {
                      setCode(t.code);
                      void opsApi.tallyCode(t.code).then(setTally).catch(() => {});
                    }}
                  >
                    {t.code} · {t.count}
                  </button>
                ))}
              </div>
            </div>
          )}

          {tally?.code != null && (
            <div>
              <p className="text-foreground mb-2 text-sm font-medium">
                Code {tally.code}: {tally.total} occurrence{tally.total === 1 ? "" : "s"}
                {tally.total > tally.rows.length && ` (showing first ${tally.rows.length})`}
              </p>
              {tally.rows.length === 0 ? (
                <p className="text-muted-foreground text-sm">No transcripts carry this code.</p>
              ) : (
                <Card className="gap-0 overflow-hidden p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Firm</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Year / Form</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tally.rows.map((r, i) => (
                        <TableRow key={`${r.taxRecordId}-${i}`}>
                          <TableCell>{r.firm}</TableCell>
                          <TableCell>{r.client}</TableCell>
                          <TableCell>
                            {r.taxYear} · {r.form}
                          </TableCell>
                          <TableCell>{r.date ?? "—"}</TableCell>
                          <TableCell className="text-right">{formatCents(r.amountCents)}</TableCell>
                          <TableCell className="text-right">
                            <Link
                              to={`/admin/ops/transcripts/${r.taxRecordId}`}
                              className="text-primary text-xs font-medium hover:underline"
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sequence mode */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sequence mode</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1">
              <Label htmlFor="seq-start">Start code</Label>
              <Input id="seq-start" value={start} onChange={(e) => setStart(e.target.value)} placeholder="150" className="w-28" />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="seq-end">End code</Label>
              <Input id="seq-end" value={end} onChange={(e) => setEnd(e.target.value)} placeholder="670" className="w-28" />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="seq-days">Within (days)</Label>
              <Input id="seq-days" type="number" value={days} onChange={(e) => setDays(e.target.value)} className="w-28" />
            </div>
            <Button type="button" onClick={() => void runSequence()} disabled={seqBusy}>
              {seqBusy ? "Matching…" : "Find sequences"}
            </Button>
          </div>

          {seq && (
            <div>
              <p className="text-foreground mb-2 text-sm font-medium">
                {seq.start} → {seq.end} within {seq.days} days: {seq.matchCount} match
                {seq.matchCount === 1 ? "" : "es"}
              </p>
              {seq.matches.length === 0 ? (
                <p className="text-muted-foreground text-sm">No transcripts match this sequence.</p>
              ) : (
                <Card className="gap-0 overflow-hidden p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Firm</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Year / Form</TableHead>
                        <TableHead>Start</TableHead>
                        <TableHead>End</TableHead>
                        <TableHead className="text-right">Gap (days)</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seq.matches.map((m, i) => (
                        <TableRow key={`${m.taxRecordId}-${i}`}>
                          <TableCell>{m.firm}</TableCell>
                          <TableCell>{m.client}</TableCell>
                          <TableCell>
                            {m.taxYear} · {m.form}
                          </TableCell>
                          <TableCell>{m.startDate}</TableCell>
                          <TableCell>{m.endDate}</TableCell>
                          <TableCell className="text-right">{m.gapDays}</TableCell>
                          <TableCell className="text-right">
                            <Link
                              to={`/admin/ops/transcripts/${m.taxRecordId}`}
                              className="text-primary text-xs font-medium hover:underline"
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
